/**
 * Token Generator
 * 
 * Converts Property data into design tokens and CSS custom properties.
 */

import type { Property } from '@vibesites/schema';
import type { 
  DesignTokens, 
  CSSVariableMap, 
  TokenGeneratorOptions,
  ColorTokens,
} from './types';
import { defaultTokens } from './defaults';

// ============================================================================
// Color Contrast Utilities
// ============================================================================

/**
 * Parse a hex color string into RGB components.
 * Supports #RGB and #RRGGBB formats.
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  let cleaned = hex.replace('#', '');
  // Ignore alpha channel (#RGBA / #RRGGBBAA)
  if (cleaned.length === 4) cleaned = cleaned.slice(0, 3);
  if (cleaned.length === 8) cleaned = cleaned.slice(0, 6);
  if (cleaned.length === 3) {
    return {
      r: parseInt(cleaned[0] + cleaned[0], 16),
      g: parseInt(cleaned[1] + cleaned[1], 16),
      b: parseInt(cleaned[2] + cleaned[2], 16),
    };
  }
  if (cleaned.length === 6) {
    return {
      r: parseInt(cleaned.slice(0, 2), 16),
      g: parseInt(cleaned.slice(2, 4), 16),
      b: parseInt(cleaned.slice(4, 6), 16),
    };
  }
  return null;
}

/**
 * Calculate WCAG relative luminance of a color.
 * https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
function getRelativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;

  const [rs, gs, bs] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );

  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Determine the best contrasting text color (white or dark) for a given
 * background color. Uses WCAG luminance threshold.
 */
export function getContrastTextColor(bgHex: string): string {
  const luminance = getRelativeLuminance(bgHex);
  // Favor white text on medium tones (gold, tan, dusty blue) where dark text is
  // technically higher-contrast but reads as heavy/muddy. Flip to dark only on
  // genuinely light backgrounds (pastels, cream, near-white).
  return luminance > 0.55 ? '#1a1a1a' : '#ffffff';
}

// ============================================================================
// Property to Tokens Mapping
// ============================================================================

/**
 * Extract color tokens from property data.
 * Automatically computes textOnPrimary / textOnSecondary for contrast.
 */
function extractColorTokens(property: Property): Partial<ColorTokens> {
  const colors: Partial<ColorTokens> = {};

  if (property.property_primary_color) {
    colors.primary = property.property_primary_color;
    colors.textOnPrimary = getContrastTextColor(property.property_primary_color);
  }
  if (property.property_secondary_color) {
    colors.secondary = property.property_secondary_color;
    colors.accent = property.property_secondary_color;
    colors.textOnSecondary = getContrastTextColor(property.property_secondary_color);
  }
  if (property.property_font_color) {
    colors.text = property.property_font_color;
  }

  // Additional color fields from property
  if (property.property_color_3) {
    colors.surface = property.property_color_3;
  }

  return colors;
}

/**
 * Generate complete design tokens from property data.
 */
export function generateTokensFromProperty(
  property: Property,
  baseTokens: Partial<DesignTokens> = defaultTokens
): DesignTokens {
  const propertyColors = extractColorTokens(property);

  return {
    ...defaultTokens,
    ...baseTokens,
    color: {
      ...defaultTokens.color,
      ...baseTokens.color,
      ...propertyColors,
    },
  };
}

// ============================================================================
// CSS Variable Generation
// ============================================================================

/**
 * Convert a token path to a CSS variable name (kebab-case).
 * e.g., "color.textOnPrimary" -> "--vibe-color-text-on-primary"
 */
function tokenPathToCSSVar(path: string, prefix: string = 'vibe'): string {
  return `--${prefix}-${path.replace(/\./g, '-').replace(/([A-Z])/g, '-$1').toLowerCase()}`;
}

/**
 * Flatten nested tokens into CSS variable map.
 */
function flattenTokens(
  tokens: Record<string, unknown>,
  prefix: string = 'vibe',
  path: string = ''
): CSSVariableMap {
  const result: CSSVariableMap = {};

  for (const [key, value] of Object.entries(tokens)) {
    const currentPath = path ? `${path}.${key}` : key;

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      Object.assign(result, flattenTokens(value as Record<string, unknown>, prefix, currentPath));
    } else if (typeof value === 'string') {
      const cssVarName = tokenPathToCSSVar(currentPath, prefix);
      result[cssVarName] = value;
    }
  }

  return result;
}

/**
 * Generate CSS custom properties from design tokens.
 */
export function generateCSSVariables(
  tokens: DesignTokens,
  options: TokenGeneratorOptions = {}
): CSSVariableMap {
  const { prefix = 'vibe' } = options;
  return flattenTokens(tokens as unknown as Record<string, unknown>, prefix);
}

/**
 * Generate a CSS string with all custom properties.
 */
export function generateCSSString(
  tokens: DesignTokens,
  options: TokenGeneratorOptions = {}
): string {
  const variables = generateCSSVariables(tokens, options);
  
  const lines = Object.entries(variables)
    .map(([name, value]) => `  ${name}: ${value};`)
    .join('\n');

  return `:root {\n${lines}\n}`;
}

/**
 * Generate CSS string from property data directly.
 */
export function generatePropertyCSS(
  property: Property,
  baseTokens?: DesignTokens,
  options?: TokenGeneratorOptions
): string {
  const tokens = generateTokensFromProperty(property, baseTokens);
  return generateCSSString(tokens, options);
}

// ============================================================================
// Inline Style Generation
// ============================================================================

/**
 * Generate inline style object for React components.
 */
export function generateInlineStyles(
  tokens: DesignTokens,
  options: TokenGeneratorOptions = {}
): Record<string, string> {
  const variables = generateCSSVariables(tokens, options);
  return variables;
}

// ============================================================================
// Token Utilities
// ============================================================================

/**
 * Get a CSS variable reference for use in component styles.
 */
export function cssVar(tokenPath: string, fallback?: string): string {
  const varName = tokenPathToCSSVar(tokenPath);
  return fallback ? `var(${varName}, ${fallback})` : `var(${varName})`;
}

/**
 * Create a token reference helper for a specific prefix.
 */
export function createTokenHelper(prefix: string = 'vibe') {
  return {
    var: (path: string, fallback?: string) => {
      const varName = tokenPathToCSSVar(path, prefix);
      return fallback ? `var(${varName}, ${fallback})` : `var(${varName})`;
    },
    name: (path: string) => tokenPathToCSSVar(path, prefix),
  };
}

/**
 * Default token helper with 'vibe' prefix.
 */
export const token = createTokenHelper('vibe');

